export enum AccountType {
  ADMIN = 'admin',
  UW_MANAGER = 'uw_manager',
  UNDERWRITER = 'underwriter',
  APPRAISER = 'appraiser',
  LOCK_DESK = 'lock_desk',
  LOAN_SETUP = 'loan_setup',
  DISCLOSURE_SPEC = 'disclosure_spec',
  CLOSER = 'closer',
  POST_CLOSER = 'post_closer',
  SERVICING = 'servicing',
  DRAW_SPECIALIST = 'draw_specialist',
  FEASIBILITY_ANALYST = 'feasibility_analyst',
  ACCOUNTING = 'accounting',
  NATIONAL_SALE = 'national_sale',
  OPERATION_SUPERVISOR = 'operation_supervisor',
  VENDOR_APPROVAL = 'vendor_approval',
  ACCOUNT_MANAGER = 'account_manager',
  ACCOUNT_EXECUTIVE = 'account_executive',
  BROKER = 'broker',
  RETAIL = 'retail',
  CORRESPONDENT = 'correspondent',
  TABLEFUNDER = 'tablefunder',
  BRANCH = 'branch',
  LOAN_OFFICER = 'loan_officer',
  LOAN_PROCESSOR = 'loan_processor',
  LOAN_OFFICER_RETAIL = 'loan_officer_retail',
  LOAN_PROCESSOR_RETAIL = 'loan_processor_retail',
  VENDOR = 'vendor',
}

export enum UserStatus {
  ALL = 'all',
  Active = 'active',
  Inactive = 'inactive',
}

export type UserOrderBy = 'companyName' | 'name' | 'email' | 'phone' | 'companyNmls' | 'accountType'

/**
 * A user holds one or more account types. `accountType` carries the primary one; the arrays carry
 * the whole set. Payloads written before multi-type existed have no arrays, so both readers fall
 * back to the scalar.
 */
export const getUserAccountTypes = (user?: any): string[] => {
  if (!user) return []
  const held = [...(user.accountTypes || []), ...(user.accountTypeKeys || [])]
  if (held.length) return Array.from(new Set(held))
  const primary = user.accountTypeKey || user.accountType
  return primary ? [primary] : []
}

/** The configured account-type keys a user holds, primary first. Use this for display. */
export const getUserAccountTypeKeys = (user?: any): string[] => {
  if (!user) return []
  if (user.accountTypeKeys?.length) return user.accountTypeKeys
  if (user.accountTypes?.length) return user.accountTypes
  const primary = user.accountTypeKey || user.accountType
  return primary ? [primary] : []
}

/** True when the user holds any of `types`, matched against base types and configured keys alike. */
export const userHasAccountType = (user: any, types: AccountType | string | (AccountType | string)[]): boolean => {
  const wanted = Array.isArray(types) ? types : [types]
  if (wanted.length === 0) return false
  const held = getUserAccountTypes(user)
  return wanted.some((type) => held.includes(type as string))
}

export const VerificationAccountType = [
  AccountType.BROKER,
  AccountType.CORRESPONDENT,
  AccountType.TABLEFUNDER,
  AccountType.LOAN_OFFICER,
  AccountType.LOAN_PROCESSOR,
  AccountType.LOAN_OFFICER_RETAIL,
  AccountType.LOAN_PROCESSOR_RETAIL,
  AccountType.BRANCH,
]

export const BrokerVerificationAccountType = [AccountType.BROKER, AccountType.CORRESPONDENT, AccountType.TABLEFUNDER]

export const BrokerAccountTypes = [
  AccountType.BROKER,
  AccountType.CORRESPONDENT,
  AccountType.TABLEFUNDER,
  AccountType.RETAIL,
  AccountType.BRANCH,
]

export const UnderwriterAEAccountTypes = [
  AccountType.BROKER,
  AccountType.TABLEFUNDER,
  AccountType.CORRESPONDENT,
  AccountType.RETAIL,
  AccountType.BRANCH,
  AccountType.LOAN_OFFICER,
  AccountType.LOAN_PROCESSOR,
]

/** Third-party originators: not Nexys company staff. */
export const ExternalOriginatorAccountTypes: AccountType[] = [
  AccountType.BROKER,
  AccountType.CORRESPONDENT,
  AccountType.TABLEFUNDER,
  AccountType.BRANCH,
  AccountType.LOAN_OFFICER,
  AccountType.LOAN_PROCESSOR,
]

export interface UserFilterQuery {
  query?: string
  status?: UserStatus
  accountType?: AccountType
  pullCreditScoreLevel?: string
  accountExecutive?: string
  orderBy?: UserOrderBy
  orderDir?: 1 | -1
  skip?: number
  count?: number
  showDeleted?: boolean
  showLoanSharing?: boolean
  showOthers?: boolean
  subscribeBroadcastEmail?: boolean
}

export const UserStatusFilters = {
  all: 'All',
  inactive: 'Lead',
  pending: 'Pending',
  active: 'Active',
}

export const permissionRequireTypes = [
  // AccountType.NATIONAL_SALE,
  AccountType.ACCOUNT_EXECUTIVE,
  AccountType.BROKER,
  AccountType.RETAIL,
  AccountType.CORRESPONDENT,
  AccountType.TABLEFUNDER,
  AccountType.BRANCH,
]

export const isNeedAccountTypeValidate = (accountType: AccountType) => {
  return permissionRequireTypes.indexOf(accountType) != -1
}

export interface UserActivityFilterQuery {
  startDate?: string
  endDate?: string
  email?: string
  accountType?: string
}

export enum UserActivityType {
  LOGIN = 'login',
  FORGET_PASSWORD = 'forget_password',
  RESET_PASSWORD = 'reset_password',
  REGISTER_USER = 'register_user',
  CREATE_USER = 'create_user',
  UPDATE_USER = 'update_user',
  UPDATE_LOAN_PROPERTY = 'update_loan_property',
}

export const originalAccountTypes: Record<string, string> = {
  admin: 'Admin',
  uw_manager: 'UW Manager',
  underwriter: 'Underwriter',
  appraiser: 'Valuation Reviewer',
  loan_setup: 'Loan Setup',
  disclosure_spec: 'Disclosure Specialist',
  lock_desk: 'Lock Desk',
  national_sale: 'National Sales',
  operation_supervisor: 'Operation Supervisor',
  vendor_approval: 'Vendor Approval',
  closer: 'Closer',
  post_closer: 'Post Closer',
  servicing: 'Servicing',
  draw_specialist: 'Draw Specialist',
  feasibility_analyst: 'Feasibility Analyst',
  accounting: 'Accounting',
  account_manager: 'Account Manager',
  account_executive: 'Account Executive',
  broker: 'Broker',
  retail: 'Retail',
  correspondent: 'Correspondent',
  tablefunder: 'Table Funder',
  branch: 'Branch',
  loan_officer: 'Loan Officer - Third Party',
  loan_processor: 'Loan Processor - Third Party',
  loan_officer_retail: 'Loan Officer - Retail',
  loan_processor_retail: 'Loan Processor - Retail',
  vendor: 'Vendor',
}

/** In-use account types only (key → display title). Used for Responsibility and other dropdowns. */
export let accountTypes = { ...originalAccountTypes }

/**
 * Every configured account type title, including types not marked In Use.
 * Used so renamed-but-hidden roles still resolve on existing conditions.
 */
export let accountTypeTitles: Record<string, string> = { ...originalAccountTypes }

export let accountTypeBaseTypes: Record<string, string> = {}

let accountTypeKeysByTitle: { source: Record<string, string>; keys: Record<string, string> } | null = null

export const setAccountTypes = (data: Record<string, string>) => {
  accountTypes = data
  accountTypeKeysByTitle = null
}

export const setAccountTypeTitles = (data: Record<string, string>) => {
  accountTypeTitles = data || {}
  accountTypeKeysByTitle = null
}

export const setAccountTypeBaseTypes = (data: Record<string, string>) => {
  accountTypeBaseTypes = data || {}
}

export const getBaseAccountType = (key: string): string => accountTypeBaseTypes[key] || key

export type HeldAccountRole = { key: string; base: string }

/**
 * One voter per held account type. Cloned profile keys keep their own key so a deny stored on the
 * clone is not cancelled by an empty base-type row; built-in types use the same string for both.
 * Borrower / temporary sessions stay a single `borrower` identity.
 */
export const heldAccountRoles = (user?: any): HeldAccountRole[] => {
  if (!user) return []
  if (user.isBorrower || user.temparary) return [{ key: 'borrower', base: 'borrower' }]
  const keys = getUserAccountTypeKeys(user)
  if (!keys.length) {
    const primary = user.accountTypeKey || user.accountType
    if (!primary) return []
    return [{ key: primary, base: getBaseAccountType(primary) }]
  }
  return keys.map((key) => ({ key, base: getBaseAccountType(key) }))
}

/** Allow-list: true when any held role is listed. An empty list is unrestricted. */
export const anyHeldTypeAllowed = (user: any, allowed: string[] = []): boolean => {
  if (!allowed.length) return true
  const roles = heldAccountRoles(user)
  if (!roles.length) return false
  return roles.some((role) => allowed.includes(role.key) || allowed.includes(role.base))
}

/** Truthy flag map (floater tabs): true when any held role has an explicit true flag. */
export const anyHeldTypeFlag = (user: any, flags?: Record<string, boolean>): boolean => {
  if (!flags) return false
  const roles = heldAccountRoles(user)
  if (!roles.length) return false
  return roles.some((role) => flags[role.key] === true || flags[role.base] === true)
}

/**
 * Deny map: true when every held role explicitly denies `pageKey`. A missing entry for a role
 * allows. Lookup order per role: key entry if present, otherwise base-type entry.
 */
export const allHeldTypesDeny = (
  user: any,
  byAccountType: Record<string, Record<string, boolean>> = {},
  pageKey: string,
): boolean => {
  const roles = heldAccountRoles(user)
  if (!roles.length) return false
  return roles.every((role) => {
    const keyMap = byAccountType[role.key]
    if (keyMap && Object.prototype.hasOwnProperty.call(keyMap, pageKey)) return keyMap[pageKey] === false
    const baseMap = role.base !== role.key ? byAccountType[role.base] : undefined
    if (baseMap && Object.prototype.hasOwnProperty.call(baseMap, pageKey)) return baseMap[pageKey] === false
    return false
  })
}

/**
 * Submenu-style map where `false` means denied: hidden only when every held role is explicitly false.
 * Missing entries allow.
 */
export const allHeldTypesFlagDeny = (user: any, flags?: Record<string, boolean>): boolean => {
  if (!flags) return false
  const roles = heldAccountRoles(user)
  if (!roles.length) return false
  return roles.every((role) => {
    if (Object.prototype.hasOwnProperty.call(flags, role.key)) return flags[role.key] === false
    if (role.base !== role.key && Object.prototype.hasOwnProperty.call(flags, role.base))
      return flags[role.base] === false
    return false
  })
}

export const isExternalOriginatorAccountType = (key?: string): boolean => {
  if (!key) return false
  return ExternalOriginatorAccountTypes.includes(getBaseAccountType(key) as AccountType)
}

export const userIsExternalOriginator = (user: any): boolean =>
  getUserAccountTypes(user).some((type) => isExternalOriginatorAccountType(type))

/**
 * Assistant chats from a third-party originator stay public. A staff type held alongside one keeps
 * the staff rule: the chat starts private and they can share it.
 */
export const userChatsAreAlwaysPublic = (user: any): boolean => {
  const roles = heldAccountRoles(user)
  if (!roles.length) return false
  return roles.every((role) => isExternalOriginatorAccountType(role.key))
}

/**
 * Conditions store the responsibility as an account type *title*, while the hierarchy stores the
 * key, so renaming a role leaves them holding a title nobody holds anymore. Match a stored value
 * against the built-in defaults as well as the configured titles to get back to the key.
 */
const titleToKeyMap = (): Record<string, string> => {
  if (accountTypeKeysByTitle?.source === accountTypeTitles) return accountTypeKeysByTitle.keys

  const keys: Record<string, string> = {}
  // Configured titles (including hidden) win over built-in originals when both map to a title.
  Object.keys(originalAccountTypes).forEach((key) => (keys[originalAccountTypes[key]] = key))
  Object.keys(accountTypeTitles).forEach((key) => (keys[accountTypeTitles[key]] = key))
  Object.keys(accountTypes).forEach((key) => (keys[accountTypes[key]] = key))
  accountTypeKeysByTitle = { source: accountTypeTitles, keys }
  return keys
}

/** The account type key behind a stored value, which may already be a key or may be a stale title. */
export const resolveAccountTypeKey = (value?: string): string => {
  if (!value) return ''
  if (accountTypes[value] || accountTypeTitles[value] || originalAccountTypes[value]) return value
  return titleToKeyMap()[value] || ''
}

/** The currently configured title for a stored value; unknown values are shown as they are. */
export const resolveAccountTypeTitle = (value?: string): string => {
  const key = resolveAccountTypeKey(value)
  return accountTypes[key] || accountTypeTitles[key] || originalAccountTypes[key] || value || ''
}

export const syncAccountTypesFromConfig = (
  config: Record<string, { title: string; visible: boolean; baseType?: string }>,
) => {
  const resolveBase = (key: string): string => {
    let current = key
    const seen = new Set<string>()
    while (config[current]?.baseType && !seen.has(current)) {
      seen.add(current)
      current = config[current].baseType!
    }
    return current
  }

  const visibleTitles: Record<string, string> = {}
  const allTitles: Record<string, string> = {}
  const baseTypes: Record<string, string> = {}
  Object.entries(config).forEach(([key, item]) => {
    allTitles[key] = item.title
    if (!item.visible) return
    visibleTitles[key] = item.title
    if (item.baseType) baseTypes[key] = resolveBase(key)
  })
  // Do not merge originalAccountTypes — unchecked types must stay out of dropdowns.
  setAccountTypes(visibleTitles)
  setAccountTypeTitles(allTitles)
  setAccountTypeBaseTypes(baseTypes)
}

export const accountTypeWithBorrower = (): Record<string, any> => ({ ...accountTypes, borrower: 'Borrower' })

export const accountTypeWithBorrowerAndNonLoggedUser = (): Record<string, any> => ({
  ...accountTypes,
  borrower: 'Borrower',
  non_loggedin_user: 'Non Logged In User',
})

export const getAssignedToAccountTypes = () =>
  [
    ...Object.keys(accountTypes),
    'loan_borrower',
    'self_account_executive',
    'self_underwriter',
    'self_broker',
    'self_loan_officer',
    'self_loan_processor',
    'self_account_manager',
    'self_creator',
    'self_appraiser',
    'self_closer',
  ].filter((v) => Object.keys(accountTypes).includes(v) || v.startsWith('self_') || v === 'loan_borrower')

export const accountTypeOptions = (accountType: string): Record<string, string> => {
  let rlt = accountTypes
  switch (accountType) {
    case AccountType.ACCOUNT_EXECUTIVE:
    case AccountType.NATIONAL_SALE:
      rlt = {
        broker: accountTypes[AccountType.BROKER],
        retail: accountTypes[AccountType.RETAIL],
        correspondent: accountTypes[AccountType.CORRESPONDENT],
        tablefunder: accountTypes[AccountType.TABLEFUNDER],
        branch: accountTypes[AccountType.BRANCH],
        loan_officer: accountTypes[AccountType.LOAN_OFFICER],
        loan_processor: accountTypes[AccountType.LOAN_PROCESSOR],
      }
      break
    case AccountType.BROKER:
    case AccountType.RETAIL:
    case AccountType.CORRESPONDENT:
    case AccountType.TABLEFUNDER:
      rlt = {
        branch: accountTypes[AccountType.BRANCH],
        loan_officer: accountTypes[AccountType.LOAN_OFFICER],
        loan_processor: accountTypes[AccountType.LOAN_PROCESSOR],
      }
      break
    case AccountType.BRANCH:
      rlt = {
        loan_officer: accountTypes[AccountType.LOAN_OFFICER],
        loan_processor: accountTypes[AccountType.LOAN_PROCESSOR],
      }
      break
  }
  Object.keys(rlt).forEach((key) => {
    if (!rlt[key]) delete rlt[key]
  })
  return rlt
}
